import type { ComponentPropsWithoutRef } from 'react'
import { forwardRef } from 'react'

import { cn } from '@adverity/ds-tokens/cn'

import './circular-progress.css'

type CircularProgressSize = 'xx-small' | 'x-small' | 'small' | 'medium' | 'large' | 'x-large'

type CircularProgressProps = Omit<ComponentPropsWithoutRef<'svg'>, 'style'> & {
    size?: CircularProgressSize
}

const sizeClassMap: Record<CircularProgressSize, string> = {
    'xx-small': 'size-x-small',
    'x-small': 'size-small',
    small: 'size-medium',
    medium: 'size-large',
    large: 'size-x-large',
    'x-large': 'size-xx-large',
}

const strokeWidthMap: Record<CircularProgressSize, number> = {
    'xx-small': 16,
    'x-small': 16,
    small: 8,
    medium: 8,
    large: 8,
    'x-large': 8,
}

const RADIUS = 50

const CircularProgress = forwardRef<SVGSVGElement, CircularProgressProps>(
    (
        {
            size = 'small', // @lintDefault
            className,
            role = 'status',
            'aria-label': ariaLabel = 'Loading',
            ...props
        },
        ref,
    ) => {
        const strokeWidth = strokeWidthMap[size]
        const actualRadius = RADIUS - strokeWidth / 2
        const circumference = 2 * Math.PI * actualRadius

        return (
            <svg
                ref={ref}
                viewBox="0 0 100 100"
                fill="none"
                role={role}
                aria-label={ariaLabel}
                className={cn('text-brand inline-block shrink-0', sizeClassMap[size], className)}
                {...props}
            >
                <circle
                    cx={RADIUS}
                    cy={RADIUS}
                    r={actualRadius}
                    strokeWidth={strokeWidth}
                    className="ds-circular-progress-spin stroke-background-emphasis"
                />
                <circle
                    cx={RADIUS}
                    cy={RADIUS}
                    r={actualRadius}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    stroke="currentcolor"
                    strokeDasharray={`${circumference} ${circumference}`}
                    strokeDashoffset={circumference * 0.75}
                    className="ds-circular-progress-spin"
                />
            </svg>
        )
    },
)

CircularProgress.displayName = 'CircularProgress'

export { CircularProgress }
export type { CircularProgressProps, CircularProgressSize }
